import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { pricingRules, serviceOptions, optionLabels } from "@/data/pricing";

const PriceSimulator = () => {
  const [formData, setFormData] = useState({
    service: "",
    intervention: "",
    largeur: "",
    hauteur: "",
    surface: "",
    urgence: "standard",
    options: [],
  });

  const selectedService = serviceOptions.find((option) => option.value === formData.service);

  const surfaceValue = useMemo(() => {
    if (!selectedService?.requiresDimensions) return 0;
    const largeur = parseFloat(formData.largeur || "0");
    const hauteur = parseFloat(formData.hauteur || "0");
    const surface = parseFloat(formData.surface || "0");
    if (surface) return surface;
    if (largeur && hauteur) return largeur * hauteur;
    return 0;
  }, [formData, selectedService]);

  const estimation = useMemo(() => {
    if (!formData.service || !formData.intervention) return null;
    const rule = pricingRules[formData.service];
    if (!rule) return null;
    const base = rule.base[formData.intervention] || 0;
    const sizeCost = rule.perM2 ? surfaceValue * rule.perM2 : 0;
    const optionsCost = formData.options.reduce(
      (sum, option) => sum + (rule.options?.[option] || 0),
      0,
    );
    let total = base + sizeCost + optionsCost;
    if (formData.urgence === "immediat") total *= 1.2;
    const min = Math.round(total * 0.85);
    const max = Math.round(total * 1.15);
    return { min, max };
  }, [formData, surfaceValue]);

  const toggleOption = (option) => {
    setFormData((prev) => {
      const exists = prev.options.includes(option);
      return {
        ...prev,
        options: exists ? prev.options.filter((item) => item !== option) : [...prev.options, option],
      };
    });
  };

  return (
    <div className="bg-white border border-brand-blue/15 rounded-sm p-6 md:p-8" data-testid="price-simulator">
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm uppercase font-semibold" data-testid="sim-service-label">
              Besoin principal
            </label>
            <select
              className="w-full border border-brand-blue/15 rounded-sm px-3 py-2"
              value={formData.service}
              onChange={(event) =>
                setFormData((prev) => ({ ...prev, service: event.target.value, options: [] }))
              }
              data-testid="sim-service-select"
            >
              <option value="">Sélectionnez</option>
              {serviceOptions.map((service) => (
                <option key={service.value} value={service.value}>
                  {service.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm uppercase font-semibold" data-testid="sim-type-label">
              Type d’intervention
            </label>
            <select
              className="w-full border border-brand-blue/15 rounded-sm px-3 py-2"
              value={formData.intervention}
              onChange={(event) => setFormData((prev) => ({ ...prev, intervention: event.target.value }))}
              data-testid="sim-type-select"
            >
              <option value="">Sélectionnez</option>
              <option value="depannage">Dépannage</option>
              <option value="remplacement">Remplacement</option>
              <option value="installation">Installation</option>
            </select>
          </div>
          {selectedService?.requiresDimensions && (
            <div className="grid md:grid-cols-3 gap-3" data-testid="sim-dimensions">
              <div>
                <label className="block text-xs uppercase font-semibold">Largeur (m)</label>
                <input
                  className="w-full border border-brand-blue/15 rounded-sm px-3 py-2"
                  value={formData.largeur}
                  onChange={(event) => setFormData((prev) => ({ ...prev, largeur: event.target.value }))}
                  data-testid="sim-width"
                />
              </div>
              <div>
                <label className="block text-xs uppercase font-semibold">Hauteur (m)</label>
                <input
                  className="w-full border border-brand-blue/15 rounded-sm px-3 py-2"
                  value={formData.hauteur}
                  onChange={(event) => setFormData((prev) => ({ ...prev, hauteur: event.target.value }))}
                  data-testid="sim-height"
                />
              </div>
              <div>
                <label className="block text-xs uppercase font-semibold">Surface (m²)</label>
                <input
                  className="w-full border border-brand-blue/15 rounded-sm px-3 py-2"
                  value={formData.surface}
                  onChange={(event) => setFormData((prev) => ({ ...prev, surface: event.target.value }))}
                  data-testid="sim-surface"
                />
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm uppercase font-semibold" data-testid="sim-urgency-label">
              Niveau d’urgence
            </label>
            <select
              className="w-full border border-brand-blue/15 rounded-sm px-3 py-2"
              value={formData.urgence}
              onChange={(event) => setFormData((prev) => ({ ...prev, urgence: event.target.value }))}
              data-testid="sim-urgency-select"
            >
              <option value="standard">Standard</option>
              <option value="immediat">Immédiat (24h)</option>
            </select>
          </div>
          {selectedService?.options?.length > 0 && (
            <div className="space-y-2" data-testid="sim-options">
              <p className="text-sm uppercase font-semibold">Options</p>
              <div className="flex flex-wrap gap-2">
                {selectedService.options.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => toggleOption(option)}
                    className={`px-3 py-2 rounded-full text-xs uppercase border ${
                      formData.options.includes(option)
                        ? "border-brand-red bg-brand-red/10"
                        : "border-brand-blue/15"
                    }`}
                    data-testid={`sim-option-${option}`}
                  >
                    {optionLabels[option] || option}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="bg-brand-blue/5 border border-brand-blue/15 rounded-sm p-6" data-testid="sim-result">
          <h3 className="font-heading text-2xl uppercase" data-testid="sim-result-title">
            Estimation indicative
          </h3>
          {estimation ? (
            <div>
              <p className="text-3xl font-semibold text-brand-red mt-4" data-testid="sim-result-range">
                {estimation.min}€ – {estimation.max}€
              </p>
              <p className="text-sm text-brand-ink/70 mt-2" data-testid="sim-result-note">
                Montants indicatifs à partir de votre sélection. Un devis précis est établi après diagnostic.
              </p>
            </div>
          ) : (
            <p className="text-sm text-brand-ink/70 mt-4" data-testid="sim-result-empty">
              Sélectionnez un service et un type d’intervention pour obtenir une estimation.
            </p>
          )}
          <div className="mt-6 flex flex-col gap-3">
            <Link
              to="/contact"
              className="bg-brand-red text-white rounded-full py-3 text-xs uppercase font-semibold text-center"
              data-testid="sim-quote"
            >
              Demander un devis
            </Link>
            <a
              href="tel:0184160307"
              className="border border-brand-blue/40 rounded-full py-3 text-xs uppercase font-semibold text-center"
              data-testid="sim-call"
            >
              Appelez maintenant
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceSimulator;
